import { z } from 'zod';
import { DisplayReferenceSchema } from '../domain';

import { PartialPersonSchema } from '../person';

export const SchemeSchema = z.object({
  administrator: z.string().optional(),
  schemeCode: z.string(),
  schemeName: z.string(),
});
export type Scheme = z.infer<typeof SchemeSchema>;

export const MedicalAidSchema = SchemeSchema.extend({
  plan: z.string(),
  membershipNumber: z.string(),
});
export type MedicalAid = z.infer<typeof MedicalAidSchema>;

export const MedicalAidMembershipSchema = z.object({
  medicalAid: MedicalAidSchema,
  dependantCode: z.string(),
  isEligibleAsNextOfKin: z.boolean(),
  isMainMember: z.boolean(),
  isActive: z.boolean(),
  start: z.string(),
  end: z.string().optional(),
  status: z.string(),
});
export type MedicalAidMembership = z.infer<typeof MedicalAidMembershipSchema>;

export const MedicalAidPersonSchema = PartialPersonSchema.extend({
  medicalAidMembership: MedicalAidMembershipSchema.partial().optional(),
});
export type MedicalAidPerson = z.infer<typeof MedicalAidPersonSchema>;

export const MedicalAidValidationResponseSchema = z.array(
  MedicalAidPersonSchema.partial(),
);
export type MedicalAidValidationResponse = z.infer<
  typeof MedicalAidValidationResponseSchema
>;

export const MedicalAidValidationMembershipNumberRequestSchema = z.object({
  schemeCode: z.string(),
  membershipNumber: z.string(),
});

export const MedicalAidValidationIdNumberRequestSchema = z.object({
  schemeCode: z.string(),
  idNumber: z.string(),
});

export type MedicalAidValidationRequest =
  | MedicalAidValidationMembershipNumberRequest
  | MedicalAidValidationIdNumberRequest;

export const MedicalAidValidationRequestApiSchema = z.object({
  schemeCode: z.string(),
  idNumber: z.string().optional(),
  membershipNumber: z.string().optional(),
});

export const AdministratorSchema = z.object({
  administratorCode: z.string(),
  administratorName: z.string(),
});
export type Administrator = z.infer<typeof AdministratorSchema>;

export const GetAdministratorsResponseSchema = z.object({
  administrators: AdministratorSchema.array(),
});
export type GetAdministratorsResponse = z.infer<
  typeof GetAdministratorsResponseSchema
>;

export const GetSchemesResponseSchema = z.object({
  schemes: SchemeSchema.array(),
});
export type GetSchemesResponse = z.infer<typeof GetSchemesResponseSchema>;

const coverageStatus = [
  'active',
  'cancelled',
  'draft',
  'entered-in-error',
] as const;
/**
 * Medical aid membership details for a patient - represented in FHIR as a Coverage Resource
 */
export const PatientMedicalAidMembershipSchema = z.object({
  mainMember: DisplayReferenceSchema,
  patient: DisplayReferenceSchema,
  scheme: DisplayReferenceSchema,
  status: z.enum(coverageStatus),
  memberNumber: z.string(),
  dependantCode: z.string(),
  plan: z.string(),
});
export type PatientMedicalAidMembership = z.infer<
  typeof PatientMedicalAidMembershipSchema
>;

export type MedicalAidValidationMembershipNumberRequest = z.infer<
  typeof MedicalAidValidationMembershipNumberRequestSchema
>;
export type MedicalAidValidationIdNumberRequest = z.infer<
  typeof MedicalAidValidationIdNumberRequestSchema
>;

export type MedicalAidValidationRequestApi = z.infer<
  typeof MedicalAidValidationRequestApiSchema
>;
